import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${window.location.origin}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, error, expired
  const [message, setMessage] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendStatus, setResendStatus] = useState(null);

  useEffect(() => {
    const token = searchParams.get("token");
    
    if (!token) {
      setStatus("error");
      setMessage("No verification token provided. Please check your email link.");
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { token });
      if (response.data.success) {
        setStatus("success");
        setMessage(response.data.message || "Your email has been verified successfully!");
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || "Verification failed";
      if (errorDetail.includes("expired")) {
        setStatus("expired");
        setMessage("Your verification link has expired. Please request a new one.");
      } else {
        setStatus("error");
        setMessage(errorDetail);
      }
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;

    try {
      setResendStatus("loading");
      const response = await axios.post(`${API}/auth/resend-verification`, { email: resendEmail });
      setResendStatus("success");
    } catch (error) {
      setResendStatus("error");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="w-full max-w-md">
        <div className="card p-8 text-center">
          {/* Verifying State */}
          {status === "verifying" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: 'var(--accent-purple)', opacity: 0.1 }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-purple)' }} />
              </div>
              <h1 className="text-h3 font-display font-bold mb-2">Verifying Your Email</h1>
              <p className="text-body text-muted">Please wait while we verify your email address...</p>
            </>
          )}

          {/* Success State */}
          {status === "success" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-h3 font-display font-bold mb-2">Email Verified!</h1>
              <p className="text-body text-muted mb-6">{message}</p>
              <p className="text-body text-muted mb-6">Check your inbox for a welcome email with tips to get started.</p>
              <button
                onClick={() => navigate("/dashboard")}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}

          {/* Error State */}
          {status === "error" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-h3 font-display font-bold mb-2">Verification Failed</h1>
              <p className="text-body text-muted mb-6">{message}</p>
              <div className="space-y-3">
                <Link to="/login" className="btn-primary w-full flex items-center justify-center gap-2">
                  Back to Login
                </Link>
              </div>
            </>
          )}

          {/* Expired State */}
          {status === "expired" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(234, 179, 8, 0.1)' }}>
                <Mail className="w-8 h-8 text-yellow-500" />
              </div>
              <h1 className="text-h3 font-display font-bold mb-2">Link Expired</h1>
              <p className="text-body text-muted mb-6">{message}</p>
              
              {/* Resend Form */}
              <form onSubmit={handleResendVerification} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" style={{ pointerEvents: 'none' }} />
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="input-field pl-12 w-full"
                    required
                    data-testid="resend-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={resendStatus === "loading"}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="resend-verification-btn"
                >
                  {resendStatus === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Resend Verification Email"
                  )}
                </button>
              </form>

              {resendStatus === "success" && (
                <p className="mt-4 text-sm text-green-500">
                  If this email is registered, a new verification link has been sent.
                </p>
              )}
              {resendStatus === "error" && (
                <p className="mt-4 text-sm text-red-500">
                  Failed to resend verification email. Please try again.
                </p>
              )}

              <div className="mt-6 pt-6" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <Link to="/login" className="text-accent hover:underline">
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
